import { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  Timestamp,
} from "firebase/firestore";
import { CartPerson } from "./useCartFullData";

interface UseFirebaseCartsResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export interface Cart {
  id: string;
  createdAt:
    | Timestamp
    | { seconds: number; nanoseconds: number }
    | { _seconds: number; _nanoseconds: number };
  displayName: string;
  imageUrl: string;
  isFixedLocation: boolean;
  cartState: string;
  location:
    | {
        _latitude: number;
        _longitude: number;
      }
    | any;
  liveLocation?:
    | {
        _latitude: number;
        _longitude: number;
      }
    | any;
  merchantId: string;
  owner: {
    email: string;
    id: string;
    phoneNumber: string;
    photoUrl?: string;
    userId: string;
    displayName?: string;
  };
  restaurantIds: string[];
  updatedAt?:
    | Timestamp
    | { seconds: number; nanoseconds: number }
    | { _seconds: number; _nanoseconds: number };
  geohash?: string;
  assignedGate?: string;
  cartType?: string;
  leaveTime?: string;
  priority?: string;
  reasonForBeingOffline?: string | null;
  score?: number;
  status?: string;
  loadingConfigurations?: Record<string, any>;
  deliveryCartsIds?: string[];
}

/**
 * Custom hook to fetch carts for a specific restaurant in real-time from Firebase Firestore
 * @param restaurantId - The restaurant ID to filter carts
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseCartsByRestaurantId = (
  restaurantId: string,
): UseFirebaseCartsResult<{ carts: Cart[] }> => {
  const [data, setData] = useState<{ carts: Cart[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      setData({ carts: [] });
      return;
    }

    setLoading(true);
    setError(null);

    const cartsRef = collection(db, "Carts");
    const q = query(
      cartsRef,
      where("restaurantIds", "array-contains", restaurantId),
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const carts = snapshot.docs.map((doc) => {
          const data = doc.data();

          return {
            id: doc.id,
            ...data,
          } as Cart;
        });

        const sortedCarts = [...carts].sort((a, b) => {
          let aTime = 0;
          let bTime = 0;

          // Get timestamp from a.createdAt
          if (a.createdAt) {
            if (typeof a.createdAt === "object") {
              if ("seconds" in a.createdAt) {
                aTime = a.createdAt.seconds || 0;
              } else if ("_seconds" in a.createdAt) {
                aTime = a.createdAt._seconds || 0;
              } else if (typeof (a.createdAt as any)?.toDate === "function") {
                aTime = Math.floor(
                  (a.createdAt as any).toDate().getTime() / 1000,
                );
              }
            }
          }

          // Get timestamp from b.createdAt
          if (b.createdAt) {
            if (typeof b.createdAt === "object") {
              if ("seconds" in b.createdAt) {
                bTime = b.createdAt.seconds || 0;
              } else if ("_seconds" in b.createdAt) {
                bTime = b.createdAt._seconds || 0;
              } else if (typeof (b.createdAt as any)?.toDate === "function") {
                bTime = Math.floor(
                  (b.createdAt as any).toDate().getTime() / 1000,
                );
              }
            }
          }

          return bTime - aTime;
        });

        setData({ carts: sortedCarts });
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [restaurantId]);

  return { data, loading, error };
};

/**
 * Custom hook to fetch all carts with optional filtering
 */
export const useFirebaseCarts = (options?: {
  cartState?: string;
  status?: string;
  limit?: number;
}): UseFirebaseCartsResult<{ carts: Cart[] }> => {
  const [data, setData] = useState<{ carts: Cart[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const cartsRef = collection(db, "Carts");
    let q = query(cartsRef);

    if (options?.cartState) {
      q = query(q, where("cartState", "==", options.cartState));
    }
    if (options?.status) {
      q = query(q, where("status", "==", options.status));
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        let carts = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as Cart,
        );

        // Sort client-side by createdAt
        carts = carts.sort((a, b) => {
          let aTime = 0;
          let bTime = 0;

          if (a.createdAt) {
            if ("seconds" in a.createdAt) aTime = a.createdAt.seconds || 0;
            else if ("_seconds" in a.createdAt)
              aTime = a.createdAt._seconds || 0;
            else if (typeof (a.createdAt as any)?.toDate === "function") {
              aTime = Math.floor(
                (a.createdAt as any).toDate().getTime() / 1000,
              );
            }
          }

          if (b.createdAt) {
            if ("seconds" in b.createdAt) bTime = b.createdAt.seconds || 0;
            else if ("_seconds" in b.createdAt)
              bTime = b.createdAt._seconds || 0;
            else if (typeof (b.createdAt as any)?.toDate === "function") {
              bTime = Math.floor(
                (b.createdAt as any).toDate().getTime() / 1000,
              );
            }
          }

          return bTime - aTime;
        });

        if (options?.limit && options.limit > 0) {
          carts = carts.slice(0, options.limit);
        }

        setData({ carts });
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.cartState, options?.status, options?.limit]);

  return { data, loading, error };
};

/**
 * Custom hook to fetch a single cart by ID
 */
export const useFirebaseCartById = (
  cartId: string,
): UseFirebaseCartsResult<Cart> => {
  const [data, setData] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!cartId) {
      setLoading(false);
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);

    const cartDocRef = doc(db, "Carts", cartId);

    const unsubscribe = onSnapshot(
      cartDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setData({
            id: docSnap.id,
            ...docSnap.data(),
          } as Cart);
          setLoading(false);
          setError(null);
        } else {
          setData(null);
          setLoading(false);
          setError(new Error("Cart not found"));
        }
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [cartId]);

  return { data, loading, error };
};

/**
 * Custom hook to fetch carts by status
 */
export const useFirebaseCartsByStatus = (
  status: string,
): UseFirebaseCartsResult<{ carts: Cart[] }> => {
  const [data, setData] = useState<{ carts: Cart[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!status) {
      setLoading(false);
      setData({ carts: [] });
      return;
    }

    setLoading(true);
    setError(null);

    const cartsRef = collection(db, "Carts");
    const q = query(cartsRef, where("status", "==", status));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const carts = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as Cart,
        );

        const sortedCarts = carts.sort((a, b) => {
          let aTime = 0,
            bTime = 0;
          if (a.createdAt) {
            if ("seconds" in a.createdAt) aTime = a.createdAt.seconds || 0;
            else if ("_seconds" in a.createdAt)
              aTime = a.createdAt._seconds || 0;
          }
          if (b.createdAt) {
            if ("seconds" in b.createdAt) bTime = b.createdAt.seconds || 0;
            else if ("_seconds" in b.createdAt)
              bTime = b.createdAt._seconds || 0;
          }
          return bTime - aTime;
        });

        setData({ carts: sortedCarts });
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [status]);

  return { data, loading, error };
};

/**
 * Custom hook to fetch carts by cartState
 */
export const useFirebaseCartsByState = (
  cartState: string,
): UseFirebaseCartsResult<{ carts: Cart[] }> => {
  const [data, setData] = useState<{ carts: Cart[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!cartState) {
      setLoading(false);
      setData({ carts: [] });
      return;
    }

    setLoading(true);
    setError(null);

    const cartsRef = collection(db, "Carts");
    const q = query(cartsRef, where("cartState", "==", cartState));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const carts = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as Cart,
        );

        const sortedCarts = carts.sort((a, b) => {
          let aTime = 0,
            bTime = 0;
          if (a.createdAt) {
            if ("seconds" in a.createdAt) aTime = a.createdAt.seconds || 0;
            else if ("_seconds" in a.createdAt)
              aTime = a.createdAt._seconds || 0;
          }
          if (b.createdAt) {
            if ("seconds" in b.createdAt) bTime = b.createdAt.seconds || 0;
            else if ("_seconds" in b.createdAt)
              bTime = b.createdAt._seconds || 0;
          }
          return bTime - aTime;
        });

        setData({ carts: sortedCarts });
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [cartState]);

  return { data, loading, error };
};

/**
 * Custom hook to fetch a single cart owner by ID
 */
export const useFirebaseCartOwnerById = (
  id: string,
): UseFirebaseCartsResult<CartPerson> => {
  const [data, setData] = useState<CartPerson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);

    const cartPersonDocRef = doc(db, "CartPersons", id);

    const unsubscribe = onSnapshot(
      cartPersonDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setData({
            id: docSnap.id,
            ...docSnap.data(),
          } as any);
          setLoading(false);
          setError(null);
        } else {
          setData(null);
          setLoading(false);
          setError(new Error("Cart owner not found"));
        }
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [id]);
  return { data, loading, error };
};
